import { ticketLib } from "@agentcall/core/lib"
import { ticketType } from "@agentcall/core/type"
import { format } from "date-fns"
import { useEffect, useRef } from "react"
import type { TicketModels } from "@/api/client/ticket"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Tabs } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { SupportHistoryItem } from "./SupportHistoryItem"

type TicketComment = TicketModels.TicketGet200TicketTicketCommentsItem

interface SupportHistoryProps {
  historyTab: "supportHistory" | "updateHistory"
  onHistoryTabChange: (value: "supportHistory" | "updateHistory") => void
  onAddSupportHistory: () => void
  onEditSupportHistory?: (comment: TicketComment) => void
  onDeleteSupportHistory?: (comment: TicketComment) => void
  ticketComments?: TicketComment[]
  /** 進捗追加ボタンを表示するか */
  showAddButton?: boolean
}

// ステータスのラベル変換
function getStatusLabel(status: TicketComment["status"]): string {
  const found = ticketType.TICKET_STATUSES.find((item) => item.key === status)
  return found?.value || status
}

function getContactNameWithText(comment: TicketComment, direction: "from" | "to"): string {
  const contactName = ticketLib.getTicketContactName(comment, direction)
  const contactText = direction === "from" ? comment.contactFromText : comment.contactToText
  const trimmedContactText = contactText?.trim()

  if (!trimmedContactText || contactName.includes(trimmedContactText)) {
    return contactName
  }

  return `${contactName}（${trimmedContactText}）`
}

// ticketCommentを表示用のアイテムに変換
function convertToDisplayItem(comment: TicketComment, index: number) {
  return {
    index: index + 1,
    type: `${ticketLib.getContactTypeLabel(comment.contactType)}（${getStatusLabel(comment.status)}）`,
    datetime: format(new Date(comment.contactAt), "yyyy-MM-dd HH:mm"),
    fromPerson: getContactNameWithText(comment, "from"),
    toPerson: getContactNameWithText(comment, "to"),
    content: comment.content,
    fromCategory: comment.contactFromCategory,
    toCategory: comment.contactToCategory,
    originalComment: comment,
  }
}

export function SupportHistory({
  historyTab,
  onHistoryTabChange,
  onAddSupportHistory,
  onEditSupportHistory,
  onDeleteSupportHistory,
  ticketComments = [],
  showAddButton = true,
}: SupportHistoryProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // ticketCommentsを表示用に変換（連絡日時の古い順＝新しいものが下）
  const displayItems = [...ticketComments]
    .sort((a, b) => {
      const contactAtDiff = new Date(a.contactAt).getTime() - new Date(b.contactAt).getTime()
      return contactAtDiff || a.id - b.id
    })
    .map((comment, index) => convertToDisplayItem(comment, index))

  // 初期表示時・データ変更時にスクロールを最下部に設定
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
    }
  }, [])

  return (
    <Card className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <Tabs
        items={[
          { value: "supportHistory", label: "対応履歴" },
          { value: "updateHistory", label: "更新履歴", disabled: true },
        ]}
        value={historyTab}
        onValueChange={(v) => onHistoryTabChange(v as "supportHistory" | "updateHistory")}
        className="shrink-0 overflow-hidden rounded-t-lg"
      />
      <div
        className={cn("hidden min-h-0 flex-1 flex-col", historyTab === "supportHistory" && "flex")}
      >
        <div className="flex min-h-0 flex-1 flex-col p-4">
          <div ref={scrollContainerRef} className="min-h-0 flex-1 overscroll-contain overflow-auto">
            {displayItems.length > 0 ? (
              <div className="space-y-4">
                {displayItems.map((item) => (
                  <SupportHistoryItem
                    key={item.index}
                    {...item}
                    onEdit={
                      onEditSupportHistory
                        ? () => onEditSupportHistory(item.originalComment)
                        : undefined
                    }
                    onDelete={
                      onDeleteSupportHistory
                        ? () => onDeleteSupportHistory(item.originalComment)
                        : undefined
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="text-center text-sm text-slate-400 py-8">対応履歴はありません</div>
            )}
          </div>
          {showAddButton && (
            <div className="mt-4 flex shrink-0 justify-end">
              <Button variant="outline" size="sm" onClick={onAddSupportHistory}>
                進捗追加
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className={cn("hidden min-h-0 flex-1", historyTab === "updateHistory" && "block")}>
        <div className="p-4">TODO: フェーズ2対応</div>
      </div>
    </Card>
  )
}
